import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { EventBus, PDFLinkService, PDFFindController, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faChevronLeft, faChevronRight, faSearch,
    faMagnifyingGlassPlus, faMagnifyingGlassMinus, faDownload, faPrint,
} from '@fortawesome/free-solid-svg-icons';

import 'pdfjs-dist/web/pdf_viewer.css';
import './PdfReader.scss';

// Full-screen PDF reader built directly on pdfjs-dist's own viewer widgets (PDFViewer,
// PDFFindController, PDFLinkService) — the same rendering layer Mozilla's own viewer uses —
// rather than a third-party wrapper library: at the time this was written, every maintained
// React PDF-viewer package pins a pdfjs-dist peer range well behind the version react-pdf
// already installs here, which would've forced a second, conflicting pdfjs-dist copy into
// the bundle. Portalled to <body> for the same reason as InputDate's calendar: an overlay
// this size needs to escape any ancestor's overflow/z-index rather than being clipped by it.
export default function PdfReader({ show, fileUrl, fileName = 'document.pdf', onClose }) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const pdfViewerRef = useRef(null);
    const eventBusRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageInput, setPageInput] = useState('1');
    const [pageCount, setPageCount] = useState(0);
    const [scalePct, setScalePct] = useState(100);
    const [loading, setLoading] = useState(true);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [matchInfo, setMatchInfo] = useState({ current: 0, total: 0 });

    useEffect(() => { setPageInput(String(pageNumber)); }, [pageNumber]);

    useEffect(() => {
        if (!show || !fileUrl) return undefined;
        setLoading(true);
        setPageNumber(1);
        setPageCount(0);
        setSearchOpen(false);
        setSearchQuery('');
        setMatchInfo({ current: 0, total: 0 });

        const eventBus = new EventBus();
        const linkService = new PDFLinkService({ eventBus });
        const findController = new PDFFindController({ eventBus, linkService });
        eventBusRef.current = eventBus;

        const pdfViewer = new PDFViewer({
            container: containerRef.current,
            viewer: viewerRef.current,
            eventBus,
            linkService,
            findController,
            textLayerMode: 2,
        });
        linkService.setViewer(pdfViewer);
        pdfViewerRef.current = pdfViewer;

        const onPagesInit = () => {
            pdfViewer.currentScaleValue = 'page-width';
            setPageCount(pdfViewer.pagesCount);
            setLoading(false);
        };
        const onPageChanging = (e) => setPageNumber(e.pageNumber);
        const onScaleChanging = (e) => setScalePct(Math.round(e.scale * 100));
        const onMatchesCount = (e) => {
            if (e.matchesCount) setMatchInfo({ current: e.matchesCount.current, total: e.matchesCount.total });
        };

        eventBus.on('pagesinit', onPagesInit);
        eventBus.on('pagechanging', onPageChanging);
        eventBus.on('scalechanging', onScaleChanging);
        eventBus.on('updatefindmatchescount', onMatchesCount);

        const loadingTask = pdfjsLib.getDocument(fileUrl);
        loadingTask.promise.then((pdfDocument) => {
            pdfViewer.setDocument(pdfDocument);
            linkService.setDocument(pdfDocument);
        }).catch(() => setLoading(false));

        return () => {
            eventBus.off('pagesinit', onPagesInit);
            eventBus.off('pagechanging', onPageChanging);
            eventBus.off('scalechanging', onScaleChanging);
            eventBus.off('updatefindmatchescount', onMatchesCount);
            loadingTask.destroy();
        };
    }, [show, fileUrl]);

    useEffect(() => {
        if (!show) return undefined;
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [show, onClose]);

    if (!show) return null;

    const goToPage = (n) => {
        const viewer = pdfViewerRef.current;
        if (!viewer || !pageCount) return;
        viewer.currentPageNumber = Math.min(Math.max(1, n), pageCount);
    };

    const zoomIn = () => {
        const viewer = pdfViewerRef.current;
        if (viewer) viewer.currentScale = Math.min((viewer.currentScale || 1) + 0.1, 5);
    };
    const zoomOut = () => {
        const viewer = pdfViewerRef.current;
        if (viewer) viewer.currentScale = Math.max((viewer.currentScale || 1) - 0.1, 0.25);
    };
    const zoomFitWidth = () => {
        const viewer = pdfViewerRef.current;
        if (viewer) viewer.currentScaleValue = 'page-width';
    };

    const runSearch = (query, { again = false, findPrevious = false } = {}) => {
        eventBusRef.current?.dispatch('find', {
            source: window,
            type: again ? 'again' : '',
            query,
            caseSensitive: false,
            entireWord: false,
            highlightAll: true,
            findPrevious,
        });
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        runSearch(value);
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.click();
    };

    const handlePrint = () => {
        const win = window.open(fileUrl, '_blank');
        if (win) win.addEventListener('load', () => win.print());
    };

    return createPortal(
        <div className="pdf-reader">
            <div className="pdf-reader__toolbar">
                <div className="pdf-reader__group">
                    <button type="button" className="pdf-reader__icon-btn" onClick={() => goToPage(pageNumber - 1)} disabled={pageNumber <= 1}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <input
                        className="pdf-reader__page-input"
                        type="number"
                        min={1}
                        max={pageCount || 1}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onBlur={() => goToPage(Number(pageInput) || 1)}
                        onKeyDown={(e) => { if (e.key === 'Enter') goToPage(Number(pageInput) || 1); }}
                    />
                    <span className="pdf-reader__page-count">/ {pageCount || '—'}</span>
                    <button type="button" className="pdf-reader__icon-btn" onClick={() => goToPage(pageNumber + 1)} disabled={pageNumber >= pageCount}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>

                <div className="pdf-reader__group">
                    <button type="button" className="pdf-reader__icon-btn" onClick={zoomOut} aria-label="Zoom arrière">
                        <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                    </button>
                    <button type="button" className="pdf-reader__zoom-value" onClick={zoomFitWidth} title="Ajuster à la largeur">
                        {scalePct}%
                    </button>
                    <button type="button" className="pdf-reader__icon-btn" onClick={zoomIn} aria-label="Zoom avant">
                        <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                    </button>
                </div>

                <div className="pdf-reader__group pdf-reader__search">
                    <button type="button" className="pdf-reader__icon-btn" onClick={() => setSearchOpen((o) => !o)} aria-label="Rechercher">
                        <FontAwesomeIcon icon={faSearch} />
                    </button>
                    {searchOpen && (
                        <>
                            <input
                                className="pdf-reader__search-input"
                                placeholder="Rechercher…"
                                value={searchQuery}
                                autoFocus
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') runSearch(searchQuery, { again: true, findPrevious: e.shiftKey });
                                }}
                            />
                            {searchQuery && (
                                <span className="pdf-reader__search-count">
                                    {matchInfo.total > 0 ? `${matchInfo.current}/${matchInfo.total}` : '0/0'}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="pdf-reader__group pdf-reader__group--end">
                    <button type="button" className="pdf-reader__icon-btn" onClick={handlePrint} disabled={!fileUrl} title="Imprimer" aria-label="Imprimer">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button type="button" className="pdf-reader__icon-btn" onClick={handleDownload} disabled={!fileUrl} title="Télécharger" aria-label="Télécharger">
                        <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button type="button" className="pdf-reader__icon-btn" onClick={onClose} aria-label="Fermer">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            </div>

            {loading && (
                <div className="pdf-reader__loading">
                    <div className="pdf-reader__spinner" />
                    Chargement du document…
                </div>
            )}

            <div className="pdf-reader__viewport-wrap">
                <div className="pdf-reader__viewport" ref={containerRef}>
                    <div className="pdfViewer" ref={viewerRef} />
                </div>
            </div>
        </div>,
        document.body
    );
}
